import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Lock, CreditCard, FileText, Zap, BookOpen, Star, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CTAButton } from './Shared';
const carouselImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/e2sbRNz2mHdQHVj5tQArB8/mockup_nuevo_para_shopi.png',
  alt: 'Kit Reflexo-Pro Workbook - Mockup completo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6CpTWVJkk7nDm41WntxZht/Screenshot_20260504-134638~2.png',
  alt: 'Hábitos y Contexto - Bloque 1 Página 4'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/ewaqDDtm6d8HPu4AmjzWzJ/Screenshot_20260504-134703~2.png',
  alt: 'Registro de Sesión - Bloque 2 Sesión 1'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6vMiBYGkLvzs8WLveiLBj5/Screenshot_20260504-134730~2.png',
  alt: 'Planilla: Lista Maestra de Insumos'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/p9oicoXxcVre6kNYyQR33w/Screenshot_20260504-134748~2.png',
  alt: 'Planilla: Stock General'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gfb8e5Ki3mQiaQBRQw12Z2/Screenshot_20260504-134840~2.png',
  alt: 'Planilla: Control Rápido Semanal'
}];
export const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const paginate = useCallback((newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex(prev => (prev + newDirection + carouselImages.length) % carouselImages.length);
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      paginate(1);
    }, 4000);
    return () => clearInterval(interval);
  }, [paginate]);
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9
    })
  };
  return <div className="relative overflow-hidden pt-12 pb-8 md:pt-20 md:pb-12 bg-transparent space-x-[0px]" data-id="element-108">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-pink/20 rounded-full blur-[140px] -z-10 pointer-events-none" data-id="element-109"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-110">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="space-y-[0px] bg-transparent space-x-[0px]" data-id="element-111">
          <div className="inline-flex bg-white border border-brand-pink/50 px-4 py-1.5 rounded-full mb-4 space-x-2 pt-[0px] pb-[0px] pl-[1px] pr-[1px] flex-row items-center justify-center shadow-sm" data-id="element-112">
            <span className="text-[12px]" data-id="element-113">🦶</span>
            <span className="font-serif font-semibold tracking-wider md:text-base text-[12px] text-brand-text" data-id="element-114">
              LUMEN AKASHA™
            </span>
          </div>

          <h1 className="md:text-6xl lg:text-7xl font-serif font-bold mb-2 text-[28px]" data-id="element-115">
            Documentá la evolución de tus pacientes hoy mismo
          </h1>
          <p className="md:text-2xl text-brand-rose mb-8 font-[700] font-[Georgia,_serif] pt-[10px] pb-[10px] text-[15px]" data-id="element-116">
            planillas listas para usar
          </p>

          {/* Carousel */}
          <div className="relative z-10 max-w-3xl mx-auto ml-[100px] mr-[100px] pl-[8px] pr-[8px] mt-[1px] mb-[1px]" data-id="element-117">
            <div className="relative overflow-hidden rounded-2xl bg-white/40 border border-brand-pink/20 shadow-lg pt-[100px] pb-[100px]" style={{
            aspectRatio: '3/4',
            maxHeight: '520px'
          }} data-id="element-118">
              <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-119">
                <motion.img key={currentIndex} src={carouselImages[currentIndex].src} alt={carouselImages[currentIndex].alt} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                x: {
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                },
                opacity: {
                  duration: 0.2
                }
              }} className="absolute inset-0 w-full h-full object-contain p-2" draggable={false} data-id="element-120" />
              </AnimatePresence>

              {/* Navigation arrows */}
              <button onClick={() => paginate(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 border border-brand-pink/30 flex items-center justify-center shadow-md hover:bg-white transition-colors z-10" aria-label="Anterior" data-id="element-121">
                <ChevronLeftIcon className="w-5 h-5 text-brand-rose" data-id="element-122" />
              </button>
              <button onClick={() => paginate(1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 border border-brand-pink/30 flex items-center justify-center shadow-md hover:bg-white transition-colors z-10" aria-label="Siguiente" data-id="element-123">
                <ChevronRightIcon className="w-5 h-5 text-brand-rose" data-id="element-124" />
              </button>
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-4" data-id="element-125">
              {carouselImages.map((_, i) => <button key={i} onClick={() => {
              setDirection(i > currentIndex ? 1 : -1);
              setCurrentIndex(i);
            }} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === currentIndex ? 'bg-brand-rose w-6' : 'bg-brand-pink/40 hover:bg-brand-pink/60'}`} aria-label={`Ir a imagen ${i + 1}`} data-id="element-126" />)}
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-8 space-y-3 px-4" data-id="element-127">
            <p className="md:text-xl font-bold text-brand-rose font-[Georgia,_serif] text-[12px]" data-id="element-128">
              El sistema completo para terapeutas en reflexología que necesitan
              registrar, organizar y hacer seguimiento profesional de sus
              pacientes
            </p>
            <p className="md:text-base text-brand-text/80 text-[12px] font-[Inter,_sans-serif]" data-id="element-129">
              Guía clínica + planillas de registro + control de stock. Todo en
              un solo kit. Descargá hoy y usalo mañana.
            </p>
          </div>

          <div className="flex flex-wrap md:gap-6 mb-12 text-xs md:text-sm font-medium gap-[12px] items-start justify-center" data-id="element-130">
            <div className="flex bg-white px-4 py-2 rounded-full border border-brand-pink/30 whitespace-nowrap text-[9px] pt-[6px] pb-[6px] pl-[10px] pr-[10px] mt-[0px] ml-[0px] items-start justify-start shadow-sm" data-id="element-131">
              <FileText className="w-4 h-4 mr-2 text-brand-rose shrink-0" data-id="element-132" />
              100% PDF
            </div>
            <div className="flex items-center bg-white px-4 py-2 rounded-full border border-brand-pink/30 whitespace-nowrap text-[9px] pl-[10px] pr-[10px] ml-[0px] shadow-sm" data-id="element-133">
              <Zap className="w-4 h-4 mr-2 text-brand-rose shrink-0" data-id="element-134" />
              Acceso en 5 min
            </div>
            <div className="flex items-center bg-white px-4 py-2 rounded-full border border-brand-pink/30 whitespace-nowrap text-[9px] pb-[9px] mb-[10px] shadow-sm" data-id="element-135">
              <span className="mr-2 text-brand-rose shrink-0" data-id="element-136">🇦🇷</span>
              Hecho para Argentina
            </div>
            <div className="flex items-center bg-white px-4 py-2 rounded-full border border-brand-pink/30 whitespace-nowrap text-[9px] pb-[9px] mb-[10px] shadow-sm" data-id="element-137">
              <span className="mr-2 text-brand-rose shrink-0" data-id="element-138">🦶</span>
              Todos los niveles de práctica
            </div>
          </div>

          <div className="flex flex-col items-center space-y-6" data-id="element-139">
            <CTAButton className="text-xl px-10 py-5" data-id="element-140">
              🦶 → QUIERO MI KIT REFLEXO-PRO AHORA
            </CTAButton>

            <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-sm text-brand-muted" data-id="element-141">
              <div className="flex items-center text-[10px]" data-id="element-142">
                Garantía 7 días📥 Descarga inmediata · 🖨️ Listo para imprimir
              </div>
              <div className="flex items-center text-[10px]" data-id="element-143">
                <Lock className="w-4 h-4 mr-1.5" data-id="element-144" /> Pago seguro
              </div>
              <div className="flex items-center text-[10px]" data-id="element-145">
                <CreditCard className="w-4 h-4 mr-1.5" data-id="element-146" /> Mercado Pago
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Social Proof Bar */}
      <div className="mt-12 border-y border-brand-pink/20 bg-white/50 py-4 pt-[1px] pb-[1px]" data-id="element-147">
        {/* Mobile: wrapped grid */}
        <div className="md:hidden flex flex-wrap justify-center gap-x-6 gap-y-2 px-4 text-[13px]" data-id="element-148">
          <div className="flex items-center text-[16px]" data-id="element-149">
            <span className="font-bold text-brand-rose mr-1.5" data-id="element-150">+200</span>
            terapeutas organizados
          </div>
          <div className="flex items-center text-[16px]" data-id="element-151">
            <span className="font-bold text-brand-rose mr-1.5" data-id="element-152">1</span>
            Tomo listo
          </div>
          <div className="flex items-center text-[16px]" data-id="element-153">
            <span className="font-bold text-brand-rose mr-1.5" data-id="element-154">2</span> Bonos
            incluidos
          </div>
          <div className="flex items-center text-[16px]" data-id="element-155">
            <Star className="w-3.5 h-3.5 text-brand-rose fill-brand-rose mr-1" data-id="element-156" />
            4.9/5 calificación
          </div>
        </div>

        {/* Desktop: horizontal row with dots */}
        <div className="hidden md:flex flex-row items-center space-x-6 justify-center whitespace-nowrap px-4" data-id="element-157">
          <div className="flex items-center text-[15px]" data-id="element-158">
            <span className="font-bold text-brand-rose mr-2" data-id="element-159">+200</span>
            terapeutas organizados
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-rose/30 shrink-0" data-id="element-160"></div>
          <div className="flex items-center text-[15px]" data-id="element-161">Tomo listo</div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-rose/30 shrink-0" data-id="element-162"></div>
          <div className="flex items-center text-[15px]" data-id="element-163">
            <span className="font-bold text-brand-rose mr-2" data-id="element-164">2</span> Bonos
            incluidos
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-rose/30 shrink-0" data-id="element-165"></div>
          <div className="flex items-center text-[15px]" data-id="element-166">
            <Star className="w-4 h-4 text-brand-rose fill-brand-rose mr-1" data-id="element-167" />
            4.9/5 calificación
          </div>
        </div>
      </div>
    </div>;
};